import type {
  FlowState,
  StepData,
} from '../providers/dynamicExperienceReducer';
import type {
  BuildLeadObjectInputs,
  BuildLeadObjectInputsValue,
} from './buildLeadObject';
import type { Step, Option, MultiQuestion, Question } from '../types/types';
import { StepType } from '../types/enums';
import { isPii } from './isPii';

// Helper function to collect all unique leadFieldAssociated values from steps
export const getAllLeadFieldsFromFlow = (steps: Step[]): string[] => {
  const leadFields = new Set<string>();

  steps.forEach((step) => {
    // Handle regular steps
    if (step.leadFieldAssociated) {
      leadFields.add(step.leadFieldAssociated as string);
    }

    // Handle multi-question children
    if (step.type === StepType.MULTI_QUESTION) {
      const mq = step as MultiQuestion;
      (mq.stepQuestions || []).forEach((q: Question) => {
        if (q.leadFieldAssociated) {
          leadFields.add(q.leadFieldAssociated as string);
        }
      });
    }
  });

  return Array.from(leadFields);
};

export const getAllSelectedOptions = (
  currentFlowState: FlowState,
): Option[] => {
  return Object.values(currentFlowState).flatMap((stepData) => {
    if (!stepData?.option) return [];
    const options = Array.isArray(stepData.option)
      ? stepData.option
      : [stepData.option];
    return options.map((opt) => ({
      ...opt,
      leadFieldAssociated:
        opt.leadFieldAssociated ||
        (stepData.leadFieldAssociated as string) ||
        '',
    }));
  });
};

// Dynamic function to get all current field values
export const getCurrentFieldValues = (steps: Step[], flowState: FlowState) => {
  const allLeadFields = getAllLeadFieldsFromFlow(steps);
  const fieldValues: Record<string, string> = {};

  // Get current values for all lead fields dynamically
  for (const fieldName of allLeadFields) {
    try {
      const value = getDataByFieldFromFlow(fieldName, flowState);
      if (value && value !== '') {
        fieldValues[fieldName] = value;
      }
    } catch (error) {
      console.warn(`Failed to get data for field: ${fieldName}`, error);
    }
  }

  return fieldValues;
};

const VALUE_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'phoneNumber',
  'zip',
  'zipCode',
];

export const getDataByFieldFromFlow = (field: string, flowState: FlowState) => {
  const allOptions = getAllSelectedOptions(flowState);

  const option = allOptions.find((opt) => opt.leadFieldAssociated === field);

  if (VALUE_FIELDS.includes(field)) {
    return option?.value?.toString();
  }

  return option?.text?.toString();
};

/**
 * Gets DCS values (degree, subject, category, career) from flow state
 * Used by both translateStepPlaceholders and InfoBanner for consistent DCS value resolution
 */
export const getDcsValues = (
  flowState: FlowState,
): { degree: string; subject: string; category: string; career: string } => {
  return {
    degree: getDataByFieldFromFlow('degree', flowState) || '',
    subject: getDataByFieldFromFlow('subject', flowState) || '',
    category: getDataByFieldFromFlow('category', flowState) || '',
    career: getDataByFieldFromFlow('career', flowState) || '',
  };
};

export const getFullOptionsFromFlowState = (
  field: string,
  flowState: FlowState,
) => {
  const allOptions = getAllSelectedOptions(flowState);

  const option = allOptions.find((opt) => opt.leadFieldAssociated === field);

  return (
    option || { optionId: '', text: '', value: '', leadFieldAssociated: '' }
  );
};

// Fails closed: an option is PII when its own lead field or the step-level
// lead field is PII, so a non-PII option field cannot unmask a PII step field.
export const isOptionPii = (
  opt: Option | undefined,
  stepLeadField: StepData['leadFieldAssociated'] | undefined,
  step: Step | undefined,
): boolean =>
  isPii(opt?.leadFieldAssociated?.toString() || '', step, step?.stepName) ||
  isPii(stepLeadField?.toString() || '', step, step?.stepName);

// Function get all fields from a step to send in FormContinued event
export const getFields = (
  stepData: StepData,
  currentStep: Step,
  flowState: FlowState,
) => {
  // For multi-questions, get field names from child steps
  if ((currentStep as MultiQuestion)?.stepQuestions) {
    const childQuestions = (currentStep as MultiQuestion).stepQuestions || [];
    return childQuestions
      .map((childQ: Question & Step) => {
        const childStepData = flowState[childQ.id] as StepData;
        if (!childStepData?.option) return null;

        const option = Array.isArray(childStepData.option)
          ? childStepData.option[0]
          : childStepData.option;

        const shouldSanitize = isOptionPii(
          option,
          childStepData?.leadFieldAssociated,
          childQ as Step,
        );

        return {
          fieldName:
            childQ?.stepName ||
            childStepData?.leadFieldAssociated?.toString() ||
            childQ?.title?.toString() ||
            '',
          fieldValue: shouldSanitize
            ? 'SANITIZED'
            : option?.text?.toString() || option?.value?.toString() || '',
        };
      })
      .filter(
        (field): field is { fieldName: string; fieldValue: string } =>
          field !== null,
      );
  }
  const shouldSanitize = isOptionPii(
    Array.isArray(stepData?.option) ? undefined : stepData?.option,
    stepData?.leadFieldAssociated,
    currentStep,
  );

  return Array.isArray(stepData?.option)
    ? stepData?.option?.map((opt) => ({
        fieldName:
          currentStep?.stepName ||
          opt?.leadFieldAssociated?.toString() ||
          currentStep?.title?.toString() ||
          '',
        fieldValue: isOptionPii(opt, stepData?.leadFieldAssociated, currentStep)
          ? 'SANITIZED'
          : opt?.text?.toString() || opt?.value?.toString() || '',
      }))
    : [
        {
          fieldName:
            currentStep?.stepName ||
            stepData?.leadFieldAssociated?.toString() ||
            currentStep?.title?.toString() ||
            '',
          fieldValue: shouldSanitize
            ? 'SANITIZED'
            : stepData?.option?.text?.toString() ||
              stepData?.option?.value?.toString() ||
              '',
        },
      ];
};

// Function get all fields from the flow state to send in FormSubmitted event
export const getAllFlowFields = (flowState: FlowState, steps: Step[]) => {
  return Object.entries(flowState)
    .filter(([stepId, stepData]) => {
      const step = steps.find((s) => s.id === stepId);
      // Exclude TCPA and parent multi-question steps (their children are already included)
      return (
        stepId !== 'TCPA' &&
        (stepData?.option || stepId === 'location') &&
        step?.type !== StepType.MULTI_QUESTION
      );
    })
    .flatMap(([stepId, stepData]) => {
      // Handle location as a special case
      if (stepId === 'location') {
        const loc = stepData as Record<string, unknown>;
        const locationFields = [];
        if (loc.state) {
          locationFields.push({
            fieldName: 'location',
            fieldValue: loc.state as string,
          });
        }
        if (loc.ipInferredPostalCode) {
          locationFields.push({
            fieldName: 'location',
            fieldValue: loc.ipInferredPostalCode as string,
          });
        }
        return locationFields;
      }

      const step = steps.find((s) => s.id === stepId);

      // For DCS steps, return degree, category, subject as separate fields
      if (step?.type === StepType.DCS) {
        return [
          {
            fieldName: 'degree',
            fieldValue:
              (getFullOptionsFromFlowState('degree', flowState)
                ?.value as string) || '',
          },
          {
            fieldName: 'category',
            fieldValue:
              (getFullOptionsFromFlowState('category', flowState)
                ?.value as string) || '',
          },
          {
            fieldName: 'subject',
            fieldValue:
              (getFullOptionsFromFlowState('subject', flowState)
                ?.value as string) || '',
          },
        ];
      }

      // For other steps
      const options = Array.isArray(stepData?.option)
        ? stepData?.option
        : [stepData?.option];

      return options.map((opt) => ({
        fieldName:
          stepData?.leadFieldAssociated?.toString() ||
          step?.stepName ||
          step?.title?.toString() ||
          stepId,
        fieldValue: isOptionPii(opt, stepData?.leadFieldAssociated, step)
          ? 'SANITIZED'
          : opt?.text?.toString() || opt?.value?.toString() || '',
      }));
    });
};

export const transformFlowStateToLeadInputs = (
  flowState: FlowState,
): BuildLeadObjectInputs => {
  const inputs: Partial<BuildLeadObjectInputs> = {};

  Object.entries(flowState).forEach(([stepId, stepData]) => {
    if (!stepData) return;

    // Handle location as a plain object (state, ipInferredPostalCode)
    if (stepId === 'location') {
      const loc = stepData as Record<string, unknown>;
      if (loc.state !== undefined) {
        (
          inputs as Record<string, BuildLeadObjectInputsValue<string, unknown>>
        ).state = { key: 'state', value: loc.state as string };
      }
      if (loc.ipInferredPostalCode !== undefined) {
        (
          inputs as Record<string, BuildLeadObjectInputsValue<string, unknown>>
        ).ipInferredPostalCode = {
          key: 'ipInferredPostalCode',
          value: loc.ipInferredPostalCode as string,
        };
      }
      return;
    }

    if (!stepData.option) return;
    const option = stepData.option;

    if (Array.isArray(option)) {
      // Handle option arrays (e.g. DCS steps store degree/category/subject
      // as separate options each with their own leadFieldAssociated)
      option.forEach((opt) => {
        if (opt.leadFieldAssociated && opt.value !== undefined) {
          (
            inputs as Record<
              string,
              BuildLeadObjectInputsValue<string, unknown>
            >
          )[opt.leadFieldAssociated] = {
            key: opt.leadFieldAssociated,
            value: opt.value,
          };
        }
      });
    } else {
      const key =
        (stepData.leadFieldAssociated as string) || option.leadFieldAssociated;
      const value = option.value;
      if (key && value !== undefined) {
        (inputs as Record<string, BuildLeadObjectInputsValue<string, unknown>>)[
          key
        ] = { key, value };
      }
    }
  });

  return inputs as BuildLeadObjectInputs;
};
